import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Ticket, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["General", "Account & Login", "Matches & Messaging", "Payments & Subscription", "Safety & Reporting", "Technical Issue"];

const inputCls = "bg-[#0F172A] border-white/[0.15] focus:border-[#E11D48] focus-visible:ring-[#E11D48]/30 text-white placeholder:text-slate-500 rounded-xl";

export default function Support() {
  const [form, setForm] = useState({ email: "", subject: "", category: "General", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/tickets", form);
      setResult(data);
      toast.success("Ticket submitted! We'll reply to your email.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center spark-fade-up">
          <span className="mx-auto flex items-center justify-center w-16 h-16 rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30">
            <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
          </span>
          <h1 className="mt-6 font-heading text-3xl font-bold text-white" data-testid="ticket-success-title">Ticket received</h1>
          <p className="mt-3 text-slate-400">
            Thanks for reaching out. Our support team will review your ticket and reply to{" "}
            <span className="text-white">{result.email}</span>.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-[#2A313C] bg-[#1F242D] px-5 py-3">
            <span className="text-sm text-slate-400">Reference</span>
            <span className="font-mono font-semibold text-[#E11D48]" data-testid="ticket-ref">{result.ref}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(result.ref); toast.success("Copied"); }}
              className="text-slate-400 hover:text-white"
              data-testid="copy-ref"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-8">
            <Button
              onClick={() => { setResult(null); setForm({ email: "", subject: "", category: "General", message: "" }); }}
              data-testid="new-ticket-btn"
              className="rounded-xl bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white hover:opacity-90"
            >
              Submit another ticket
            </Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2A313C] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#E11D48]">
            <Ticket className="w-3.5 h-3.5" /> Support
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-white">Submit a support ticket</h1>
          <p className="mt-3 text-slate-400">Tell us what's going on and we'll get back to you by email.</p>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-[#2A313C] bg-[#1F242D] p-6 sm:p-8 space-y-5" data-testid="support-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300">Your email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")}
              placeholder="you@example.com" className={inputCls} data-testid="ticket-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-slate-300">Subject</Label>
            <Input id="subject" required value={form.subject} onChange={set("subject")}
              placeholder="Brief summary of your issue" className={inputCls} data-testid="ticket-subject" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Category</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger className={inputCls} data-testid="ticket-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#262D38] border-[#2A313C] text-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="focus:bg-[#E11D48]/15 focus:text-white">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-300">Message</Label>
            <Textarea id="message" required rows={6} value={form.message} onChange={set("message")}
              placeholder="Describe your issue in detail..." className={inputCls} data-testid="ticket-message" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="ticket-submit"
            className="w-full rounded-xl py-6 text-base font-semibold spark-gradient text-white hover:shadow-[0_0_24px_rgba(225,29,72,0.4)] transition-all">
            {submitting ? "Submitting..." : "Submit ticket"}
          </Button>
        </form>
      </div>
    </PublicLayout>
  );
}
